import { Box, Button, CircularProgress, Modal, Typography, Checkbox, Divider, Accordion, AccordionDetails, AccordionGroup, AccordionSummary } from "@mui/joy";
import React, { useState, useEffect } from "react";
import { palette, theme } from "../../theme";
import RichText from "../rich-text";
import { CourseCondition, ConditionType, UserSignatureStatusResponse } from "../../store/api/privacy-policy-api";
import { ExpandMore } from "@mui/icons-material";
import TaugaLogo from "../../images/logo.png";

// TODO: Fix TypeScript errors with optional string fields in CourseCondition interface
// Lines 188, 220: Need to handle undefined values for privacyConditionText and userConditionText

interface SignConditionsResponse {
  success: boolean;
  message?: string;
}

interface CourseConditionsModalProps {
  open: boolean;
  conditions?: CourseCondition;
  signatureStatus?: UserSignatureStatusResponse;
  loading: boolean;
  onSignConditions: () => Promise<SignConditionsResponse | void>; // Single handler for all signing scenarios
}

/**
 * Modal that shows course conditions (privacy policy and user conditions) with dropdown sections and checkboxes.
 * Users must explicitly check agreement boxes before the sign button becomes enabled.
 */
export default function CourseConditionsModal({
  open,
  conditions,
  signatureStatus,
  loading,
  onSignConditions,
}: CourseConditionsModalProps) {
  const [isSigning, setIsSigning] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [userAccepted, setUserAccepted] = useState(false);

  // Reset checkbox states when modal opens or conditions change
  useEffect(() => {
    if (open) {
      setPrivacyAccepted(false);
      setUserAccepted(false);
    }
  }, [open, conditions?.id, conditions?.version]);

  // Helper functions to check signature status for each type using real backend individual tracking
  const needsPrivacySignature = (): boolean => {
    if (!signatureStatus || !conditions) return false;
    
    // Must be visible to be shown
    if (!conditions.privacyConditionVisible) return false;
    
    const bothForceReSignFalse = !conditions.privacyConditionForceReSign && !conditions.userConditionForceReSign;
    const versionMismatch = signatureStatus.signedVersion !== signatureStatus.currentVersion;
    const privacyForceReSign = !!conditions.privacyConditionForceReSign;
    
    // Case 1: Both force re-sign variables are false AND version mismatch
    if (bothForceReSignFalse && versionMismatch) {
      return true;
    }
    
    // Case 2: Privacy force re-sign is true (regardless of versions)
    if (privacyForceReSign) {
      return true;
    }
    
    return false;
  };

  const needsUserSignature = (): boolean => {
    if (!signatureStatus || !conditions) return false;
    
    // Must be visible to be shown
    if (!conditions.userConditionVisible) return false;
    
    const bothForceReSignFalse = !conditions.privacyConditionForceReSign && !conditions.userConditionForceReSign;
    const versionMismatch = signatureStatus.signedVersion !== signatureStatus.currentVersion;
    const userForceReSign = !!conditions.userConditionForceReSign;
    
    // Case 1: Both force re-sign variables are false AND version mismatch
    if (bothForceReSignFalse && versionMismatch) {
      return true;
    }
    
    // Case 2: User force re-sign is true (regardless of versions)
    if (userForceReSign) {
      return true;
    }
    
    return false;
  };

  const hasSignedPrivacy = (): boolean => {
    if (!signatureStatus) return false;
    
    // Use the new enhanced backend response format first
    if (signatureStatus.individual?.privacy) {
      return signatureStatus.individual.privacy.signed;
    }
    
    // Fallback to legacy field
    if (signatureStatus.hasSignedPrivacy !== undefined) {
      return !!signatureStatus.hasSignedPrivacy;
    }
    
    return false; // Conservative fallback
  };

  const hasSignedUser = (): boolean => {
    if (!signatureStatus) return false;
    
    // Use the new enhanced backend response format first
    if (signatureStatus.individual?.user) {
      return signatureStatus.individual.user.signed;
    }
    
    // Fallback to legacy field
    if (signatureStatus.hasSignedUser !== undefined) {
      return !!signatureStatus.hasSignedUser;
    }
    
    return false; // Conservative fallback
  };

  const needsPrivacy = needsPrivacySignature();
  const needsUser = needsUserSignature();

  // Show conditions if they need to be signed (visibility is already checked in needsSignature functions)
  const showPrivacyCondition = needsPrivacy;
  const showUserCondition = needsUser;

  // Don't show modal if no conditions exist or they're not active/visible
  if (!conditions || !conditions.isActive || !conditions.visibility) {
    return null;
  }

  if (!showPrivacyCondition && !showUserCondition) {
    return null;
  }

  // Only show modal if at least one condition needs signing
  if (!needsPrivacySignature() && !needsUserSignature()) {
    return null;
  }

  const handleSign = async () => {
    setIsSigning(true);
    try {
      const response = await onSignConditions();
      
      // Removed auto-signing notification – backend no longer performs auto-signing.
    } catch (e) {
      console.error("Error signing conditions:", e);
      // Error is handled in the parent component
    } finally {
      setIsSigning(false);
    }
  };

  // Removed auto-signing notification – backend no longer performs auto-signing.


  // Determine if sign button should be enabled
  const isSignButtonEnabled = () => {
    if (needsPrivacy && needsUser) {
      return privacyAccepted && userAccepted;
    } else if (needsPrivacy) {
      return privacyAccepted;
    } else if (needsUser) {
      return userAccepted;
    }
    return false;
  };



    const renderConditionDropdown = (
    title: string,
    conditionText: string,
    isAccepted: boolean,
    onAcceptedChange: (accepted: boolean) => void,
    needsSigning: boolean,
    conditionType?: ConditionType
  ) => {
    if (!needsSigning) return null;
    
    return (
      <Box sx={{ mb: 4 }}>
        <AccordionGroup>
          <Accordion
            sx={{
              border: `1px solid ${palette.purple}20`,
              borderRadius: '12px !important',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                borderColor: `${palette.purple}40`,
                transform: 'translateY(-1px)'
              },
              '&.Mui-expanded': {
                boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                borderColor: `${palette.purple}60`
              }
            }}
          >
            <AccordionSummary 
              indicator={<ExpandMore />}
              sx={{
                bgcolor: `${palette.purple}05`,
                '&:hover': {
                  bgcolor: `${palette.purple}10`
                },
                '& .MuiAccordionSummary-content': {
                  margin: '16px 0'
                }
              }}
            >
                             <Typography 
                 level="title-md" 
                 sx={{ 
                   fontWeight: 'bold',
                   background: `linear-gradient(135deg, ${palette.purple} 0%, ${palette.blue} 100%)`,
                   backgroundClip: 'text',
                   WebkitBackgroundClip: 'text',
                   WebkitTextFillColor: 'transparent'
                 }}
               >
                 {title}
               </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                transition: 'all 0.3s ease'
              }}
            >
              <Box sx={{ 
                maxHeight: '300px', 
                overflowY: 'auto',
                border: `1px solid ${palette.purple}20`,
                borderRadius: 8,
                p: 3,
                bgcolor: 'rgba(248, 250, 252, 0.8)',
                backdropFilter: 'blur(10px)',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  width: '6px'
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '3px'
                },
                                 '&::-webkit-scrollbar-thumb': {
                   backgroundColor: `${palette.purple}50`,
                   borderRadius: '3px',
                   '&:hover': {
                     backgroundColor: `${palette.purple}80`
                   }
                 }
              }}>
                <RichText content={conditionText} />
              </Box>
            </AccordionDetails>
          </Accordion>
        </AccordionGroup>

        <Box sx={{ 
          mt: 3,
          p: 3,
          bgcolor: isAccepted ? `${palette.green_light}60` : `${palette.gray_extra_light}`,
          borderRadius: 12,
          border: '2px solid',
          borderColor: isAccepted ? `${palette.green_finish}60` : `${palette.purple}30`,
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: 'translateY(0)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          '&:hover': {
            borderColor: isAccepted ? `${palette.green_finish}80` : `${palette.purple}50`,
            bgcolor: isAccepted ? `${palette.green_light}80` : `${palette.purple_light}40`,
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
          },
          '&:active': {
            transform: 'translateY(0)'
          }
        }}
        onClick={() => onAcceptedChange(!isAccepted)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Checkbox
              checked={isAccepted}
              onChange={(event) => onAcceptedChange(event.target.checked)}
              color="success"
              size="lg"
              sx={{ 
                pointerEvents: 'auto',
                transform: 'scale(1.1)',
                '& .MuiCheckbox-checkbox': {
                  transition: 'all 0.2s ease'
                }
              }}
            />
            <Typography 
              level="body-lg" 
              sx={{ 
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: isAccepted ? 'bold' : 'medium',
                color: isAccepted ? palette.green_finish : 'inherit',
                transition: 'all 0.3s ease'
              }}
              onClick={(e) => {
                e.stopPropagation();
                onAcceptedChange(!isAccepted);
              }}
            >
              אני מסכים/ה ל{title.toLowerCase()}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Modal 
      open={open} 
      disableEscapeKeyDown
      sx={{
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(0, 0, 0, 0.6)'
      }}
    >
      <Box
        sx={{
          position: "absolute" as const,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: {
            xs: "90%",
            md: "70%",
            lg: "60%",
          },
          maxHeight: "85vh",
          bgcolor: palette.white,
          borderRadius: 20,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1)",
          p: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflow: "hidden",
          border: `1px solid ${palette.purple}20`,
          animation: 'modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes modalSlideIn': {
            '0%': { 
              opacity: 0, 
              transform: 'translate(-50%, -50%) scale(0.9) translateY(20px)' 
            },
            '100%': { 
              opacity: 1, 
              transform: 'translate(-50%, -50%) scale(1) translateY(0)' 
            }
          }
        }}
      >
        {/* Tauga Logo */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mb: 3,
          position: 'relative'
        }}>
          <Box sx={{
            position: 'absolute',
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${palette.purple}20 0%, ${palette.purple_light}80 100%)`,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { transform: 'translate(-50%, -50%) scale(1)', opacity: 0.7 },
              '50%': { transform: 'translate(-50%, -50%) scale(1.05)', opacity: 0.4 }
            }
          }} />
          <Box
            component="img"
            src={TaugaLogo}
            alt="Tauga Logo"
            sx={{
              height: 70,
              objectFit: 'contain',
              position: 'relative',
              zIndex: 1,
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.15))'
              }
            }}
          />
        </Box>

        <Typography 
          level="h2" 
          textAlign="center" 
          sx={{ 
            mb: 4,
            background: `linear-gradient(135deg, ${palette.purple} 0%, ${palette.blue} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold',
            letterSpacing: '0.5px'
          }}
        >
          תנאי משתמש ומדיניות פרטיות
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size="lg" />
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
            {/* Privacy Conditions Dropdown - only show if visible AND user needs to sign */}
            {showPrivacyCondition && needsPrivacy && renderConditionDropdown(
              "מדיניות פרטיות",
              signatureStatus?.individual?.privacy?.text || conditions.privacyConditionText || '',
              privacyAccepted,
              setPrivacyAccepted,
              needsPrivacy,
              conditions.privacyConditionType
            )}

            {/* User Conditions Dropdown - only show if visible AND user needs to sign */}
            {showUserCondition && needsUser && renderConditionDropdown(
              "תנאי שימוש",
              signatureStatus?.individual?.user?.text || conditions.userConditionText || '',
              userAccepted,
              setUserAccepted,
              needsUser,
              conditions.userConditionType
            )}

            {/* Sign Button */}
            <Box sx={{ 
              pt: 4,
              mt: 4,
              display: 'flex',
              justifyContent: 'center',
              position: 'relative'
            }}>
              <Button
                variant="solid"
                color="primary"
                disabled={loading || isSigning || !isSignButtonEnabled()}
                onClick={handleSign}
                size="lg"
                sx={{ 
                  minWidth: 280,
                  height: 56,
                  borderRadius: 28,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: `linear-gradient(135deg, ${palette.purple} 0%, ${palette.blue} 100%)`,
                  boxShadow: `0 4px 16px ${palette.purple}50`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover:not(:disabled)': {
                    background: `linear-gradient(135deg, ${palette.purple_dark} 0%, ${palette.purple} 100%)`,
                    transform: 'translateY(-3px)',
                    boxShadow: `0 8px 24px ${palette.purple}60`
                  },
                  '&:active:not(:disabled)': {
                    transform: 'translateY(-1px)'
                  },
                  '&:disabled': {
                    background: `linear-gradient(135deg, ${palette.gray_light} 0%, ${palette.gray} 100%)`,
                    boxShadow: 'none'
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                    transition: 'left 0.5s',
                    pointerEvents: 'none'
                  },
                  '&:hover:not(:disabled)::before': {
                    left: '100%'
                  },
                  py: 1.5
                }}
                startDecorator={isSigning ? <CircularProgress size="sm" /> : null}
              >
                {isSigning ? 'חותם...' : 'אישור ומעבר לקורס'}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
} 